/**
 * Sprint A4.12 — Agency Overview Dashboard (tier_3 only).
 *
 * At-a-glance scorecard for all owned domains, grouped by clientLabel.
 * Domains without a label appear under "Unlabelled".
 *
 * Features:
 *  - Score coloured by health: ≥80 emerald, 50–79 amber, <50 coral
 *  - Per-row: domain, score, SEO/AEO/GEO breakdown, pending fix count, status, last audit
 *  - Row click → existing domain detail screen
 *  - Inline client label editing
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import type { DomainOverview } from '../types';

interface Props {
  onSelectDomain: (id: string) => void;
  onSelectFixes: (id: string) => void;
}

// ── Score colouring ────────────────────────────────────────────────────────

function scoreClass(score: number | null | undefined): string {
  if (score == null) return 'text-gray-400';
  if (score >= 80) return 'text-emerald-600';
  if (score >= 50) return 'text-amber-500';
  return 'text-red-500';
}

function scoreBg(score: number | null | undefined): string {
  if (score == null) return 'bg-gray-50 text-gray-400';
  if (score >= 80) return 'bg-emerald-50 text-emerald-700';
  if (score >= 50) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

// ── Helpers ────────────────────────────────────────────────────────────────

function relativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function stateLabel(state: string): string {
  const map: Record<string, string> = {
    pending:   'Pending',
    scanning:  'Scanning',
    optimised: 'Optimised',
    active:    'Active',
  };
  return map[state] ?? state;
}

function stateBadge(state: string): string {
  if (state === 'optimised' || state === 'active') return 'bg-emerald-50 text-emerald-700';
  if (state === 'scanning') return 'bg-blue-50 text-blue-700';
  return 'bg-gray-100 text-gray-500';
}

// ── Label edit cell ────────────────────────────────────────────────────────

function LabelCell({
  domainId,
  currentLabel,
  onSaved,
}: {
  domainId: string;
  currentLabel: string | null;
  onSaved: (label: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentLabel ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const label = value.trim() || null;
      await api.domains.setClientLabel(domainId, label);
      onSaved(label);
      setEditing(false);
    } catch {
      // non-critical; revert
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <input
          autoFocus
          className="border border-gray-300 rounded px-2 py-0.5 text-xs w-32 focus:outline-none focus:ring-1 focus:ring-brand-green"
          maxLength={100}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <button
          onClick={() => void save()}
          disabled={saving}
          className="text-[10px] bg-brand-green text-white px-1.5 py-0.5 rounded disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
        <button
          onClick={() => setEditing(false)}
          className="text-[10px] text-gray-400 hover:text-gray-600 px-1"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      className="text-left text-xs text-gray-500 hover:text-brand-ink group flex items-center gap-1"
    >
      {currentLabel ?? <span className="text-gray-300 italic">Unlabelled</span>}
      <span className="text-gray-300 group-hover:text-gray-400 text-[10px]">✎</span>
    </button>
  );
}

// ── Domain row ─────────────────────────────────────────────────────────────

function DomainRow({
  domain,
  onSelect,
  onSelectFixes,
  onLabelChange,
}: {
  domain: DomainOverview;
  onSelect: () => void;
  onSelectFixes: () => void;
  onLabelChange: (id: string, label: string | null) => void;
}) {
  const audit = domain.latestAudit;

  return (
    <tr
      onClick={onSelect}
      className="group cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
    >
      {/* Domain */}
      <td className="px-4 py-3">
        <div className="font-medium text-sm text-brand-ink group-hover:text-brand-green transition-colors truncate max-w-[200px]">
          {domain.domain}
        </div>
        <LabelCell
          domainId={domain.id}
          currentLabel={domain.clientLabel}
          onSaved={label => onLabelChange(domain.id, label)}
        />
      </td>

      {/* Overall score */}
      <td className="px-4 py-3 text-center">
        {audit ? (
          <span className={`inline-block text-sm font-bold px-2 py-0.5 rounded ${scoreBg(audit.score)}`}>
            {audit.score}
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* SEO / AEO / GEO */}
      <td className="px-4 py-3">
        {audit ? (
          <div className="flex gap-2 text-xs">
            <span className={scoreClass(audit.seoScore)}>SEO {audit.seoScore}</span>
            <span className="text-gray-300">·</span>
            <span className={scoreClass(audit.aeoScore)}>AEO {audit.aeoScore}</span>
            <span className="text-gray-300">·</span>
            <span className={scoreClass(audit.geoScore)}>GEO {audit.geoScore}</span>
          </div>
        ) : (
          <span className="text-xs text-gray-300">No audit yet</span>
        )}
      </td>

      {/* Pending fixes */}
      <td className="px-4 py-3 text-center">
        {domain.pendingFixes.total > 0 ? (
          <button
            onClick={e => { e.stopPropagation(); onSelectFixes(); }}
            className="inline-block text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium hover:bg-amber-100 transition-colors cursor-pointer"
          >
            {domain.pendingFixes.total}
          </button>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded ${stateBadge(domain.state)}`}>
          {stateLabel(domain.state)}
        </span>
        {!domain.verified && (
          <span className="ml-1 text-xs text-gray-300">(unverified)</span>
        )}
      </td>

      {/* Last audited */}
      <td className="px-4 py-3 text-xs text-gray-400">
        {audit ? relativeDate(audit.auditedAt) : '—'}
      </td>
    </tr>
  );
}

// ── Client group ───────────────────────────────────────────────────────────

function ClientGroup({
  label,
  domains,
  onSelect,
  onSelectFixes,
  onLabelChange,
}: {
  label: string | null;
  domains: DomainOverview[];
  onSelect: (id: string) => void;
  onSelectFixes: (id: string) => void;
  onLabelChange: (id: string, label: string | null) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  // Aggregate scores for the group header
  const audited = domains.filter(d => d.latestAudit !== null);
  const avgScore = audited.length > 0
    ? Math.round(audited.reduce((s, d) => s + (d.latestAudit?.score ?? 0), 0) / audited.length)
    : null;
  const totalFixes = domains.reduce((s, d) => s + d.pendingFixes.total, 0);

  return (
    <div className="mb-6">
      {/* Group header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 border border-gray-200 rounded-t-lg text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">{collapsed ? '▶' : '▼'}</span>
          <span className="font-semibold text-sm text-brand-ink">
            {label ?? 'Unlabelled'}
          </span>
          <span className="text-xs text-gray-400">{domains.length} domain{domains.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {avgScore !== null && (
            <span className={`font-medium ${scoreClass(avgScore)}`}>avg {avgScore}</span>
          )}
          {totalFixes > 0 && (
            <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">
              {totalFixes} pending
            </span>
          )}
        </div>
      </button>

      {!collapsed && (
        <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-white">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">Domain / Client</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase tracking-wide">Score</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">Pillars</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase tracking-wide">Fixes</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">Last audit</th>
              </tr>
            </thead>
            <tbody>
              {domains.map(d => (
                <DomainRow
                  key={d.id}
                  domain={d}
                  onSelect={() => onSelect(d.id)}
                  onSelectFixes={() => onSelectFixes(d.id)}
                  onLabelChange={onLabelChange}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function AgencyDashboard({ onSelectDomain, onSelectFixes }: Props) {
  const [domains, setDomains] = useState<DomainOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.domains
      .overview()
      .then(data => setDomains(data.domains))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load domains');
      })
      .finally(() => setLoading(false));
  }, []);

  function handleLabelChange(id: string, label: string | null) {
    setDomains(prev =>
      prev.map(d => (d.id === id ? { ...d, clientLabel: label } : d)),
    );
  }

  // Group domains by clientLabel (null → "Unlabelled" group, sorted last)
  const groups = new Map<string | null, DomainOverview[]>();
  for (const d of domains) {
    const key = d.clientLabel ?? null;
    const existing = groups.get(key) ?? [];
    existing.push(d);
    groups.set(key, existing);
  }

  // Sort groups: labelled groups alphabetically, then null last
  const sortedGroups = [...groups.entries()].sort(([a], [b]) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return a.localeCompare(b);
  });

  // Aggregate totals for page header
  const audited = domains.filter(d => d.latestAudit !== null);
  const avgScore = audited.length > 0
    ? Math.round(audited.reduce((s, d) => s + (d.latestAudit?.score ?? 0), 0) / audited.length)
    : null;
  const totalPending = domains.reduce((s, d) => s + d.pendingFixes.total, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-brand-ink mb-1">Agency Overview</h1>
        <p className="text-sm text-gray-500">All client domains at a glance. Click any row to open the domain detail.</p>
      </div>

      {/* Summary bar */}
      {!loading && !error && domains.length > 0 && (
        <div className="flex gap-6 mb-6 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Domains</p>
            <p className="text-2xl font-bold text-brand-ink">{domains.length}</p>
          </div>
          {avgScore !== null && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Avg score</p>
              <p className={`text-2xl font-bold ${scoreClass(avgScore)}`}>{avgScore}</p>
            </div>
          )}
          {totalPending > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Pending fixes</p>
              <p className="text-2xl font-bold text-amber-600">{totalPending}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Clients</p>
            <p className="text-2xl font-bold text-brand-ink">
              {[...groups.keys()].filter(k => k !== null).length}
            </p>
          </div>
        </div>
      )}

      {/* State: loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <div className="w-5 h-5 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading domains…</span>
        </div>
      )}

      {/* State: error */}
      {!loading && error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {/* State: empty */}
      {!loading && !error && domains.length === 0 && (
        <div className="py-20 text-center">
          <p className="text-gray-400 text-sm">No domains yet. Add your first domain to get started.</p>
        </div>
      )}

      {/* Groups */}
      {!loading && !error && sortedGroups.map(([label, groupDomains]) => (
        <ClientGroup
          key={label ?? '__unlabelled__'}
          label={label}
          domains={groupDomains}
          onSelect={onSelectDomain}
          onSelectFixes={onSelectFixes}
          onLabelChange={handleLabelChange}
        />
      ))}
    </div>
  );
}
